export const RELEASE_POLL_MS = 60_000;
const RETRY_RELOAD_MS = 5 * 60_000;
const ATTEMPT_KEY = "mathpop:release-reload";

export function validRelease(value) {
  return typeof value === "string" && /^(development|[a-f0-9]{40})$/.test(value);
}

function workerVersion(worker) {
  return new Promise((resolve) => {
    if (!worker) return resolve(null);
    const channel = new MessageChannel();
    const finish = (value) => { clearTimeout(timer); channel.port1.close(); resolve(value); };
    const timer = setTimeout(() => finish(null), 1000);
    channel.port1.onmessage = (event) => finish(event.data?.release ?? null);
    worker.postMessage({ type: "MATHPOP_RELEASE" }, [channel.port2]);
  });
}

export async function prepareRelease(release) {
  if (!("serviceWorker" in navigator)) return true;
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return true;
  await registration.update();
  // Wait for the new shell to be fully cached and active, never reload onto an
  // old worker or clear account storage/cookies to force an update.
  const deadline = Date.now() + 10_000;
  while (Date.now() < deadline) {
    if (await workerVersion(registration.active) === release) return true;
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
  return false;
}

export function startReleaseUpdater({
  currentVersion = document.querySelector('meta[name="mathpop-release"]')?.content,
  canReload = () => !document.querySelector('form[data-release-dirty="true"]'),
  prepare = prepareRelease,
  reload = () => location.reload(),
  fetchVersion = () => fetch("/version.json", { cache: "no-store", credentials: "same-origin", signal: AbortSignal.timeout(5000) }),
  storage,
  onPending = () => {},
} = {}) {
  if (!validRelease(currentVersion)) return { check: async () => {}, stop() {} };
  if (!storage) { try { storage = sessionStorage; } catch { /* Storage can be unavailable in restricted browsers. */ } }
  let stopped = false, checking = false, reloading = false, pending = null;
  let lastInteraction = 0;
  const interact = () => { lastInteraction = Date.now(); };
  const dirty = (event) => {
    const form = event.target.closest?.("form");
    if (form) form.dataset.releaseDirty = "true";
  };
  const safe = () => !document.hidden && Date.now() - lastInteraction >= 3000 && canReload();
  const attemptedRecently = (version) => {
    try { const last = JSON.parse(storage.getItem(ATTEMPT_KEY) || "null"); return last?.from === currentVersion && last?.to === version && Date.now() - last.at < RETRY_RELOAD_MS; }
    catch { return false; }
  };
  async function apply() {
    if (!pending || stopped || reloading || !safe() || attemptedRecently(pending)) return;
    reloading = true;
    try {
      const target = pending;
      if (!await prepare(target) || stopped || !safe()) return;
      // Recheck after worker installation: another release or a rollback may
      // have happened while the shell was downloading.
      const response = await fetchVersion();
      const latest = response.ok ? await response.json() : null;
      if (latest?.version !== target) { pending = null; return; }
      if (stopped || !safe()) return;
      try { storage.setItem(ATTEMPT_KEY, JSON.stringify({ from: currentVersion, to: target, at: Date.now() })); } catch { /* Storage may be disabled; successful reload still changes the baked version. */ }
      reload();
    } catch { /* Offline or installing: retry without disrupting play. */ }
    finally { reloading = false; }
  }
  async function check() {
    if (stopped || checking || document.hidden) return;
    checking = true;
    try {
      const response = await fetchVersion();
      if (!response.ok) return;
      const value = await response.json();
      if (!validRelease(value?.version)) return;
      pending = value.version !== currentVersion ? value.version : null;
      if (pending) onPending();
      await apply();
    } catch { /* Network errors are expected on wall tablets. */ }
    finally { checking = false; }
  }
  const visible = () => { if (!document.hidden) void check(); };
  document.addEventListener("visibilitychange", visible);
  document.addEventListener("pointerdown", interact, true);
  document.addEventListener("keydown", interact, true);
  document.addEventListener("input", dirty, true);
  document.addEventListener("change", dirty, true);
  const poll = setInterval(() => void check(), RELEASE_POLL_MS);
  const idle = setInterval(() => void apply(), 5000);
  void check();
  return { check, stop() {
    stopped = true; clearInterval(poll); clearInterval(idle);
    document.removeEventListener("visibilitychange", visible);
    document.removeEventListener("pointerdown", interact, true);
    document.removeEventListener("keydown", interact, true);
    document.removeEventListener("input", dirty, true);
    document.removeEventListener("change", dirty, true);
  } };
}
